import React from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import { i18n } from "../../translate/i18n";

const STATUS_EVENT_COLOR = {
  pending: "#c79a2b",
  confirmed: "#2b6bc7",
  completed: "#2ba85b",
  cancelled: "#8a8f98",
};

const STATUS_LEGEND = [
  { status: "pending", label: "Pendiente" },
  { status: "confirmed", label: "Confirmada" },
  { status: "completed", label: "Completada" },
  { status: "cancelled", label: "Cancelada" },
];

const useStyles = makeStyles()((theme) => {
  const primary = theme.palette.primary.main;
  const divider = theme.palette.divider;

  return {
    legend: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    dot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
    },
    calendar: {
      "& .fc": {
        "--fc-border-color": divider,
        "--fc-page-bg-color": theme.palette.background.paper,
        "--fc-neutral-bg-color": theme.palette.action.hover,
        "--fc-today-bg-color": alpha(primary, 0.1),
        "--fc-highlight-color": alpha(primary, 0.14),
        "--fc-now-indicator-color": primary,
        "--fc-button-text-color": theme.palette.text.primary,
        "--fc-button-bg-color": "transparent",
        "--fc-button-border-color": divider,
        "--fc-button-hover-bg-color": theme.palette.action.hover,
        "--fc-button-hover-border-color": divider,
        "--fc-button-active-bg-color": alpha(primary, 0.16),
        "--fc-button-active-border-color": alpha(primary, 0.5),
        color: theme.palette.text.primary,
        fontSize: 13,
      },
      "& .fc .fc-toolbar-title": {
        fontSize: 20,
        fontWeight: 700,
        textTransform: "capitalize",
      },
      "& .fc .fc-button": {
        textTransform: "capitalize",
        fontSize: 13,
        fontWeight: 600,
        padding: "6px 12px",
        boxShadow: "none",
      },
      "& .fc .fc-button:focus, & .fc .fc-button:active:focus": {
        boxShadow: "none",
      },
      "& .fc .fc-button-primary:not(:disabled).fc-button-active": {
        color: primary,
      },
      "& .fc .fc-button-primary:disabled": {
        opacity: 0.5,
      },
      "& .fc .fc-scrollgrid": {
        borderRadius: 10,
        overflow: "hidden",
      },
      "& .fc th, & .fc thead, & .fc .fc-col-header": {
        background: "transparent",
      },
      "& .fc .fc-col-header-cell": {
        padding: "8px 0",
        background: theme.palette.action.hover,
      },
      "& .fc .fc-col-header-cell-cushion": {
        color: theme.palette.text.secondary,
        fontSize: 11.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        textDecoration: "none",
      },
      "& .fc .fc-daygrid-day-number": {
        color: theme.palette.text.secondary,
        fontSize: 12.5,
        fontWeight: 600,
        textDecoration: "none",
        padding: "6px 8px",
      },
      "& .fc .fc-day-other .fc-daygrid-day-number": {
        opacity: 0.4,
      },
      "& .fc .fc-day-today .fc-daygrid-day-number": {
        color: primary,
      },
      "& .fc .fc-daygrid-day:hover": {
        background: theme.palette.action.hover,
        cursor: "pointer",
      },
      "& .fc .fc-timegrid-slot-label-cushion, & .fc .fc-timegrid-axis-cushion": {
        color: theme.palette.text.secondary,
        fontSize: 11.5,
      },
      "& .fc .fc-event": {
        borderRadius: 6,
        fontSize: 12,
        padding: "1px 4px",
      },
    },
  };
});

const AppointmentsCalendarPanel = ({
  appointments,
  onEdit,
  onNewAt,
}) => {
  const { classes } = useStyles();

  const events = appointments.map((a) => ({
    id: String(a.id),
    title: `${a.title}${a.contact ? " · " + a.contact.name : ""}`,
    start: a.startAt,
    end: a.endAt,
    backgroundColor: STATUS_EVENT_COLOR[a.status] || STATUS_EVENT_COLOR.pending,
    borderColor: STATUS_EVENT_COLOR[a.status] || STATUS_EVENT_COLOR.pending,
    extendedProps: { appointment: a },
  }));

  return (
    <div>
      <div className={classes.legend}>
        {STATUS_LEGEND.map(({ status, label }) => (
          <span key={status} className={classes.legendItem}>
            <span className={classes.dot} style={{ backgroundColor: STATUS_EVENT_COLOR[status] }} />
            {label}
          </span>
        ))}
      </div>

      <div className={classes.calendar}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={i18n.language?.startsWith("en") ? "en" : esLocale}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek",
          }}
          height="auto"
          events={events}
          selectable
          dateClick={(info) => onNewAt(info.date)}
          eventClick={(info) => onEdit(info.event.extendedProps.appointment)}
        />
      </div>
    </div>
  );
};

export default AppointmentsCalendarPanel;
